import {
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import { useLocale, useTranslations } from "next-intl";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowPathIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import {
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { BasketEvent } from "@features/cartDetails/types";
import {
  Analytics,
  EventLandingPage,
  EventShowing,
  Pricesection,
  ShowingMessage,
} from "@types";
import { AreaFormatted } from "../types";
import { AccessType, PriceStatus, ShowingGroupMode } from "@utils/enums";
import { useIsInViewport, useThemeContext, useWindowSize } from "@utils/hooks";
import {
  formatPrice,
  handlePricesectionNameEqual,
  joinClassNames,
} from "@utils/helpers";
import { useIsShopsite } from "@context/DomainContext";
import { setAreas } from "../utils/areaGrouping";
import { usePasswordValidation } from "../hooks/usePasswordValidation";
import LoadingSpinner from "@components/loadingSpinner";
import { EventDisplayGA } from "@features/eventDisplayGA";
import EventToolTip from "@features/eventPage/components/eventToolTip";
import { Badge, Card, Heading, Text } from "@components/theming";
import dynamic from "next/dynamic";
import { isEqual } from "lodash";

const AllocatingMap = dynamic(() => import("@features/eventAllocatedMap"), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center min-h-[500px]">
      <LoadingSpinner size="48" color="var(--gray-11)" />
    </div>
  ),
});

function arraysEqualAfterSorting(arr1: number[], arr2: number[]): boolean {
  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();

  return (
    sortedArr1.length === sortedArr2.length &&
    sortedArr1.every((value, index) => value === sortedArr2[index])
  );
}

interface Props {
  showing: EventShowing;
  eventId: number;
  eventName: string;
  eventVenueId: number;
  orgUid: string;
  basketEvent: BasketEvent | undefined;
  messages: ShowingMessage[];
  getPriceRange: ({
    pricesectionIds,
    groupedPricesectionIds,
  }: {
    pricesectionIds?: number[];
    groupedPricesectionIds?: number[];
  }) => { min: number; max: number; isEmpty?: boolean };
  currency: {
    code: string;
    symbol: string;
    culture: string;
  };
  selectedConnectedShowings: number[] | null;
  pricesection: Pricesection[] | undefined;
  lastAreaSelected: AreaFormatted | null;
  setLastAreaSelected: Dispatch<SetStateAction<AreaFormatted | null>>;
  createBasket?: () => Promise<void>;
  analyticsConfigs?: Analytics[];
  landingPages?: EventLandingPage[];
  onAddToCart?: () => void;
}

export default function EventAreaPicker({
  eventId,
  eventName,
  orgUid,
  showing,
  eventVenueId,
  basketEvent,
  getPriceRange,
  messages,
  currency,
  selectedConnectedShowings,
  pricesection,
  lastAreaSelected,
  setLastAreaSelected,
  createBasket,
  analyticsConfigs,
  landingPages,
  onAddToCart,
}: Props) {
  const availableAreas = showing.area;
  const showingId = showing.id ?? 0;

  const [selectedPriceSectionIds, setSelectedPriceSectionIds] = useState<
    number[] | null
  >(null);
  const locale = useLocale();
  const { targetRef, scrollToTarget } = useIsInViewport<HTMLHeadingElement>();
  const t = useTranslations("EventPage");
  const isShopsite = useIsShopsite();
  const windowSize = useWindowSize();
  const theme = useThemeContext();

  const { password, setPassword, passwordStatus } = usePasswordValidation({
    eventId: eventId,
    eventVenueId: eventVenueId,
    showingId: showingId,
    initialPassword: sessionStorage.getItem(`itk_${showing.id}_password`),
  });

  useEffect(() => {
    if (showing.access.type === AccessType.PASSWORD && password) {
      (
        document.getElementById("showing_password_form") as HTMLFormElement
      ).requestSubmit();
    }
  }, [showing.access.type, password]);

  const isPricesectionEqual = availableAreas
    ? handlePricesectionNameEqual(availableAreas)
    : false;

  const areas: AreaFormatted[] = setAreas(availableAreas, isPricesectionEqual);

  const basketTickets = basketEvent?.Showings.find(
    (showing: any) =>
      showing.Id === showingId ||
      showing.Items.some((item: any) =>
        item.ConnectedTickets?.some((ct: any) => ct.ShowingId === showingId)
      )
  )?.Items;

  const [selectedArea, setSelectedArea] = useState<AreaFormatted | null>(
    areas.length === 1
      ? areas[0]
      : lastAreaSelected &&
        areas.some((area) => isEqual(area, lastAreaSelected))
      ? lastAreaSelected
      : null
  );

  const [showAddons, setShowAddons] = useState(
    areas.length === 0 && showing.area.gaHasExtras
  );

  const area_button_count = areas.length + (showing.area.gaHasExtras ? 1 : 0);

  if (
    showing.access.type === AccessType.PASSWORD &&
    passwordStatus !== "complete"
  ) {
    const title = messages
      .filter((msg) => msg.type === "button")
      .find((msg) => msg.access === AccessType.PASSWORD)?.message;

    const description = messages
      .filter((msg) => msg.type === "message")
      .find((msg) => msg.access === AccessType.PASSWORD)?.message;

    return (
      <div
        className={joinClassNames(
          "relative overflow-hidden",
          isShopsite ? "max-sm:-mx-4 max-sm:-my-2 bg-white sm:rounded-lg" : ""
        )}
      >
        <div
          className={joinClassNames(
            "relative z-10 flex flex-col items-center justify-center gap-1 w-full h-[197px] p-4 overflow-hidden",
            isShopsite
              ? "bg-white/40 sm:rounded-lg border border-gray-200 backdrop-blur-3xl"
              : "bg-[color:var(--gray-1)] sm:rounded-[var(--radius-5)] border border-[color:var(--gray-a7)]"
          )}
        >
          <div className="flex items-center gap-1 mb-1">
            <Heading
              as="h2"
              size="6"
              weight="bold"
              shopsiteClasses="font-black text-2xl"
            >
              {title ?? t("areapicker.password.heading")}
            </Heading>
            <EventToolTip
              title={title?.trim() || t("areapicker.password.heading")}
              description={
                description?.trim() || t("areapicker.password.description")
              }
              icon={
                <LockClosedIcon className="absolute top-0 left-4 w-24 h-24 text-white/10 -rotate-12" />
              }
            />
          </div>
          <form
            id="showing_password_form"
            onSubmit={(e: SyntheticEvent<HTMLFormElement>) => {
              e.preventDefault();
              const { showing_password } = Object.fromEntries(
                new FormData(e.currentTarget)
              );
              if (showing_password) {
                setPassword(showing_password as string);
              }
            }}
          >
            <motion.div
              initial={{ x: 0 }}
              animate={
                passwordStatus === "failure"
                  ? { x: [-5, 5, -5, 5, -4, 4, -3, 3, 0] }
                  : { x: 0 }
              }
              transition={{ duration: 0.4 }}
              className={joinClassNames(
                "flex items-center gap-1 border px-2 py-1.5 ring-2 focus-within:outline-none transition-[box-shadow] ease-in-out duration-200",
                isShopsite
                  ? "rounded-md border-gray-300 bg-white text-black"
                  : "rounded-[var(--radius-3)] border-[color:var(--gray-a7)] text-[color:var(--gray-12)]",
                theme && theme.appearance === "dark"
                  ? "bg-[color:var(--gray-1)]"
                  : "bg-white",
                passwordStatus === "failure"
                  ? "border-transparent ring-red-500"
                  : isShopsite
                  ? "focus-within:border-transparent ring-transparent focus-within:ring-indigo-500"
                  : "focus-within:border-transparent ring-transparent focus-within:ring-[color:var(--accent-9)]"
              )}
            >
              <input
                id="showing_password_input"
                defaultValue={password ?? ""}
                name="showing_password"
                type="password"
                required
                className={joinClassNames(
                  "h-8 focus-visible:outline-none",
                  theme && theme.appearance === "dark"
                    ? "bg-[color:var(--gray-1)]"
                    : "bg-white"
                )}
              />
              <button
                type="submit"
                disabled={!!passwordStatus}
                className={joinClassNames(
                  "relative flex p-1 items-center justify-center",
                  isShopsite ? "rounded-full" : "rounded-[var(--radius-6)]",
                  isShopsite
                    ? passwordStatus === "loading"
                      ? "bg-gray-200"
                      : passwordStatus === "success"
                      ? "bg-emerald-100"
                      : passwordStatus === "failure"
                      ? "bg-red-100"
                      : "bg-gray-100"
                    : passwordStatus === "loading"
                    ? "bg-[color:var(--gray-4)]"
                    : passwordStatus === "success"
                    ? "bg-[color:var(--green-3)]"
                    : passwordStatus === "failure"
                    ? "bg-[color:var(--red-3)]"
                    : "bg-[color:var(--gray-3)]"
                )}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={passwordStatus}
                    initial={{ opacity: 0, width: "auto" }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: "auto" }}
                    transition={{ duration: 0.15 }}
                  >
                    {passwordStatus === "loading" ? (
                      <LoadingSpinner
                        size="24"
                        classes="p-0.5"
                        color={isShopsite ? undefined : "var(--gray-11)"}
                      />
                    ) : passwordStatus === "success" ? (
                      <CheckIcon
                        className={joinClassNames(
                          "h-6 w-6",
                          isShopsite
                            ? "text-emerald-800"
                            : "text-[color:var(--green-10)]"
                        )}
                      />
                    ) : passwordStatus === "failure" ? (
                      <XMarkIcon
                        className={joinClassNames(
                          "h-6 w-6",
                          isShopsite
                            ? "text-red-800"
                            : "text-[color:var(--red-10)]"
                        )}
                      />
                    ) : (
                      <ArrowRightIcon
                        className={joinClassNames(
                          "h-6 w-6",
                          isShopsite
                            ? "text-gray-800"
                            : "text-[color:var(--gray-10)]"
                        )}
                      />
                    )}
                  </motion.div>
                </AnimatePresence>
                <motion.div
                  className="absolute top-0 right-0"
                  animate={
                    passwordStatus === "success"
                      ? {
                          scale: [0, 1, 1, 1, 0],
                          x: [0, 8, 14, 20, 22],
                          y: [0, -8, -14, -16, -14],
                          rotate: [0, 10, 20, 30, 40],
                        }
                      : { scale: 0, x: 0, y: 0, rotate: 0 }
                  }
                >
                  {passwordStatus === "success" ? (
                    <SparklesIcon className="text-yellow-400 h-4 w-4" />
                  ) : (
                    <></>
                  )}
                </motion.div>
                <motion.div
                  className="absolute bottom-0 -left-1"
                  animate={
                    passwordStatus === "success"
                      ? {
                          scale: [0, 1, 1, 1, 0],
                          x: [0, -8, -14, -20, -22],
                          y: [0, -4, -6, -2, 2],
                          rotate: [0, -10, -20, -30, -40],
                        }
                      : { scale: 0, x: 0, y: 0, rotate: 0 }
                  }
                >
                  {passwordStatus === "success" ? (
                    <SparklesIcon className="text-yellow-400 h-4 w-4" />
                  ) : (
                    <></>
                  )}
                </motion.div>
              </button>
            </motion.div>
          </form>
        </div>
        {isShopsite ? (
          <>
            <span className="absolute top-1 right-8 w-14 h-32 rounded-full bg-yellow-400" />
            <span className="absolute top-8 right-12 w-32 h-14 rounded-full bg-fuchsia-400" />
            <span className="absolute bottom-2 right-8 w-32 h-14 rounded-full bg-sky-400" />
            <span className="absolute bottom-5 left-5 w-16 h-16 rounded-full bg-emerald-400" />
          </>
        ) : null}
      </div>
    );
  }

  function calcQuantity(type: string, area?: AreaFormatted) {
    if (!basketTickets) return 0;

    return basketTickets.reduce((prev, curr) => {
      const getpricesection = pricesection?.find(
        (ps) => ps.id === curr.PriceSectionId
      );

      const getpriceage = getpricesection?.priceages?.find(
        (pa) => pa.price.id === curr.PriceId
      );

      if (
        type === "standard" &&
        getpriceage?.type === type &&
        area?.priceSections
          .map((pricesection) => pricesection.id)
          .includes(curr.PriceSectionId)
      ) {
        return prev + 1;
      }

      if (type === "extra" && getpriceage?.type === type && getpricesection) {
        return prev + 1;
      }

      return prev;
    }, 0);
  }

  const gaPriceRange = getPriceRange({
    pricesectionIds: availableAreas.ga.map((ga) => ga.pricesection.id),
  });

  return (
    <>
      <div>
        <Heading
          as="h2"
          size="4"
          weight="bold"
          asChild
          shopsiteClasses="text-lg font-semibold"
        >
          <motion.h2
            initial={
              selectedArea || showAddons
                ? { opacity: 0, height: 0 }
                : { opacity: 1, height: 44 }
            }
            animate={
              selectedArea || showAddons
                ? { opacity: 0, height: 0 }
                : { opacity: 1, height: 44 }
            }
          >
            {t("areapicker.chooseanarea")}
          </motion.h2>
        </Heading>
        <nav
          className="isolate grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          aria-label="areas"
        >
          {areas.length > 1 || showing.area.gaHasExtras ? (
            areas.map((area) => {
              const areaBasketQuantity = calcQuantity("standard", area);
              const { min: price_range_min, max: price_range_max } =
                getPriceRange({
                  groupedPricesectionIds: area.priceSections.map(
                    (pricesection) => pricesection.id
                  ),
                });

              return (
                <div
                  key={`${area.id}-${area.name}-${area.type}`}
                  className={joinClassNames(
                    "relative",
                    showAddons ||
                      (selectedArea &&
                        (selectedArea.id !== area.id ||
                          !arraysEqualAfterSorting(
                            area.areaIds,
                            selectedArea?.areaIds ?? []
                          )))
                      ? "max-sm:hidden"
                      : ""
                  )}
                >
                  <Card
                    asChild
                    sharedClasses={joinClassNames(
                      area_button_count > 1 && area.id !== selectedArea?.id
                        ? "cursor-pointer"
                        : area.status !== PriceStatus.AVAILABLE
                        ? "cursor-not-allowed"
                        : "cursor-default",
                      "w-full"
                    )}
                    whitelabelClasses={
                      area.id === selectedArea?.id &&
                      arraysEqualAfterSorting(
                        area.areaIds,
                        selectedArea.areaIds
                      )
                        ? "bg-[color:var(--accent-9)] before:bg-[color:var(--gray-2)]"
                        : area.status !== PriceStatus.AVAILABLE
                        ? "bg-[color:var(--gray-a3)] text-[color:var(--gray-a11)]"
                        : ""
                    }
                    shopsiteClasses={joinClassNames(
                      area.id === selectedArea?.id &&
                        arraysEqualAfterSorting(
                          area.areaIds,
                          selectedArea.areaIds
                        )
                        ? "bg-indigo-50 ring-2 ring-indigo-500"
                        : area.status !== PriceStatus.AVAILABLE
                        ? "bg-gray-100 text-gray-700 border-gray-300"
                        : "bg-white border-gray-200",
                      "px-4 py-2 rounded-md border shadow transition-colors ease-in-out duration-200 h-full"
                    )}
                  >
                    <button
                      data-testid={`area-${area.id}`}
                      aria-current={
                        selectedArea?.id === area.id &&
                        arraysEqualAfterSorting(
                          area.areaIds,
                          selectedArea.areaIds
                        )
                          ? "page"
                          : undefined
                      }
                      onClick={() => {
                        setSelectedPriceSectionIds(null);

                        if (
                          selectedArea?.id === area.id &&
                          arraysEqualAfterSorting(
                            area.areaIds,
                            selectedArea.areaIds
                          ) &&
                          windowSize.width &&
                          windowSize.width > 640
                        ) {
                          return;
                        }

                        setSelectedArea(
                          selectedArea?.id === area.id &&
                            arraysEqualAfterSorting(
                              area.areaIds,
                              selectedArea.areaIds
                            ) &&
                            windowSize.width &&
                            windowSize.width <= 640
                            ? null
                            : area
                        );
                        setLastAreaSelected(
                          selectedArea?.id === area.id &&
                            arraysEqualAfterSorting(
                              area.areaIds,
                              selectedArea.areaIds
                            ) &&
                            windowSize.width &&
                            windowSize.width <= 640
                            ? null
                            : area
                        );
                        setShowAddons(false);
                        if (selectedArea?.id !== area.id) {
                          scrollToTarget();
                        }
                      }}
                      disabled={
                        area.status !== PriceStatus.AVAILABLE ||
                        area_button_count === 1
                      }
                    >
                      <div className="flex flex-col">
                        <div className="flex items-center justify-between gap-2 w-full">
                          <Text
                            as="p"
                            weight="medium"
                            align="left"
                            sharedClasses="truncate"
                            shopsiteClasses="text-left font-medium text-base"
                          >
                            {area.name}
                          </Text>
                          {area.status !== PriceStatus.AVAILABLE ? (
                            <Badge
                              color="red"
                              shopsiteClasses="!rounded-lg font-semibold"
                            >
                              <Text as="span" size="2" weight="bold">
                                {area.status === PriceStatus.NOTAVAILABLE
                                  ? t("areapicker.notavailable")
                                  : area.status === PriceStatus.SOLDOUT
                                  ? t("areapicker.soldout")
                                  : area.status === PriceStatus.BOOKEDOUT
                                  ? t("areapicker.bookedout")
                                  : t("areapicker.limitreached")}
                              </Text>
                            </Badge>
                          ) : (
                            <></>
                          )}
                        </div>
                        <div className="flex flex-col w-full">
                          <Text
                            as="span"
                            size="2"
                            sharedClasses="w-fit"
                            whitelabelClasses="text-[color:var(--accent-11)]"
                            shopsiteClasses="text-sm text-indigo-500 font-medium"
                          >
                            {area.type === "ga"
                              ? t("areapicker.ga")
                              : t("areapicker.allocated")}
                          </Text>
                          <div className="flex items-center justify-between gap-2 w-full">
                            <Text
                              as="span"
                              size="1"
                              color="gray"
                              shopsiteClasses="text-xs text-gray-500 w-fit font-medium"
                            >
                              {price_range_min !== price_range_max ? (
                                <>
                                  {formatPrice(
                                    locale,
                                    currency.code,
                                    price_range_min
                                  )}{" "}
                                  -{" "}
                                </>
                              ) : (
                                <></>
                              )}
                              {formatPrice(
                                locale,
                                currency.code,
                                price_range_max
                              )}
                            </Text>
                            {selectedArea?.id === area.id &&
                            arraysEqualAfterSorting(
                              area.areaIds,
                              selectedArea.areaIds
                            ) &&
                            ((!showAddons && areas.length > 1) ||
                              (areas.length === 1 &&
                                showing.area.gaHasExtras)) &&
                            windowSize.width &&
                            windowSize.width < 640 ? (
                              <div className="flex gap-1 items-center shrink-0">
                                <ArrowPathIcon
                                  className={joinClassNames(
                                    "h-4 w-4",
                                    isShopsite
                                      ? "text-gray-500"
                                      : "text-[color:var(--gray-a11)]"
                                  )}
                                />
                                <Text
                                  as="span"
                                  size="1"
                                  color="gray"
                                  weight="medium"
                                  shopsiteClasses="text-gray-500 text-sm font-medium"
                                >
                                  {t("areapicker.changearea")}
                                </Text>
                              </div>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  </Card>
                  <AnimatePresence>
                    {areaBasketQuantity > 0 && (
                      <motion.div
                        initial="hide"
                        animate="show"
                        exit="hide"
                        variants={{
                          hide: {
                            opacity: 0,
                          },
                          show: {
                            scale: [0, 1.2, 1],
                            opacity: 1,
                            transition: {
                              scale: {
                                type: "tween",
                                duration: 0.4,
                              },
                            },
                          },
                        }}
                        className="absolute -right-2 -top-2.5"
                      >
                        <Badge
                          variant="solid"
                          shopsiteClasses="!rounded-lg w-auto"
                        >
                          <Text
                            as="span"
                            size="1"
                            weight="medium"
                            shopsiteClasses="text-xs"
                          >
                            {t("amountincart", { amount: areaBasketQuantity })}
                          </Text>
                        </Badge>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })
          ) : (
            <></>
          )}
          {showing.area.gaHasExtras ? (
            <div className="relative">
              <Card
                asChild
                sharedClasses={joinClassNames(
                  !showAddons && selectedArea ? "max-sm:hidden" : "",
                  "w-full"
                )}
                whitelabelClasses={
                  showAddons
                    ? "ring-1 ring-[color:var(--accent-11)] cursor-default"
                    : "cursor-pointer"
                }
                shopsiteClasses={joinClassNames(
                  showAddons
                    ? "bg-indigo-50 ring-2 ring-indigo-500 cursor-default"
                    : "bg-white cursor-pointer",
                  "px-4 py-2 rounded-md border border-gray-100 shadow transition-colors ease-in-out duration-200 h-full"
                )}
              >
                <button
                  data-testid={`area-addons`}
                  aria-current={showAddons ? "page" : undefined}
                  onClick={() => {
                    if (
                      showAddons &&
                      windowSize.width &&
                      windowSize.width > 640
                    ) {
                      return;
                    }
                    setSelectedArea(null);
                    setShowAddons(!showAddons);
                    if (showAddons) {
                      scrollToTarget();
                    }
                  }}
                >
                  <div className="flex flex-col text-left">
                    <Text
                      as="p"
                      weight="medium"
                      align="left"
                      sharedClasses="truncate"
                      shopsiteClasses="text-base font-medium w-fit"
                    >
                      {t("areapicker.addons")}
                    </Text>
                    <Text
                      as="span"
                      size="2"
                      sharedClasses="w-fit"
                      whitelabelClasses="text-[color:var(--accent-11)]"
                      shopsiteClasses="text-sm text-indigo-500 font-medium"
                    >
                      {t("areapicker.extraaddons")}
                    </Text>
                    <div className="flex items-center gap-2 justify-between w-full">
                      {!gaPriceRange.isEmpty && (
                        <Text
                          as="span"
                          size="1"
                          color="gray"
                          shopsiteClasses="text-xs text-gray-500 w-fit"
                        >
                          {gaPriceRange.min !== gaPriceRange.max ? (
                            <>
                              {formatPrice(
                                locale,
                                currency.code,
                                gaPriceRange.min
                              )}{" "}
                              -{" "}
                            </>
                          ) : (
                            <></>
                          )}
                          {formatPrice(locale, currency.code, gaPriceRange.max)}
                        </Text>
                      )}
                      {showAddons &&
                      windowSize.width &&
                      windowSize.width < 640 ? (
                        <div className="flex gap-1 items-center shrink-0">
                          <ArrowPathIcon
                            className={joinClassNames(
                              "h-4 w-4",
                              isShopsite
                                ? "text-gray-500"
                                : "text-[color:var(--gray-a11)]"
                            )}
                          />
                          <Text
                            as="span"
                            size="1"
                            color="gray"
                            weight="medium"
                            shopsiteClasses="text-gray-500 text-sm font-medium"
                          >
                            {t("areapicker.changearea")}
                          </Text>
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                  {calcQuantity("extra") > 0 ? (
                    <Badge
                      variant="solid"
                      sharedClasses="absolute -right-2 -top-2.5"
                      shopsiteClasses="!rounded-lg"
                    >
                      <Text
                        as="span"
                        size="1"
                        weight="medium"
                        shopsiteClasses="text-xs"
                      >
                        {t("amountincart", { amount: calcQuantity("extra") })}
                      </Text>
                    </Badge>
                  ) : (
                    <></>
                  )}
                </button>
              </Card>
            </div>
          ) : (
            <></>
          )}
        </nav>
      </div>
      {selectedArea !== null &&
        !showAddons &&
        selectedArea.type === "allocated" &&
        selectedArea.priceSections.length > 1 && (
          <div>
            <Heading
              as="h2"
              size="4"
              weight="bold"
              sharedClasses={joinClassNames(
                "mb-4",
                areas.length > 1 || showing.area.gaHasExtras ? "mt-4" : ""
              )}
              shopsiteClasses="text-lg font-semibold text-black"
            >
              {t("areapicker.sectionsbeingshown")}
            </Heading>
            <nav
              className="isolate grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3"
              aria-label="areas"
            >
              <Card
                asChild
                sharedClasses="cursor-pointer"
                shopsiteClasses={joinClassNames(
                  "relative px-4 py-2 rounded-md border shadow border-gray-100 transition-colors ease-in-out duration-200 h-full",
                  selectedPriceSectionIds === null ||
                    (selectedPriceSectionIds &&
                      selectedPriceSectionIds.length ===
                        selectedArea.priceSections.length)
                    ? "bg-indigo-50 ring-2 ring-indigo-500"
                    : "bg-white"
                )}
                whitelabelClasses={
                  selectedPriceSectionIds === null ||
                  (selectedPriceSectionIds &&
                    selectedPriceSectionIds.length ===
                      selectedArea.priceSections.length)
                    ? "ring-1 ring-[color:var(--accent-11)]"
                    : ""
                }
              >
                <motion.button
                  onClick={() => setSelectedPriceSectionIds(null)}
                  whileTap={{
                    scale: 0.95,
                  }}
                >
                  <Text
                    as="span"
                    weight="medium"
                    shopsiteClasses="font-medium text-black"
                  >
                    {t("areapicker.allsections")}
                  </Text>
                </motion.button>
              </Card>
              {selectedArea?.priceSections.map((section) => (
                <Card
                  asChild
                  key={section.id}
                  sharedClasses="cursor-pointer"
                  shopsiteClasses={joinClassNames(
                    "relative px-4 py-2 rounded-md border shadow border-gray-100 transition-colors ease-in-out duration-200",
                    selectedPriceSectionIds &&
                      selectedPriceSectionIds.includes(section.id) &&
                      selectedPriceSectionIds.length !==
                        selectedArea.priceSections.length
                      ? "bg-indigo-50 ring-2 ring-indigo-500"
                      : "bg-white"
                  )}
                  whitelabelClasses={
                    selectedPriceSectionIds &&
                    selectedPriceSectionIds.includes(section.id) &&
                    selectedPriceSectionIds.length !==
                      selectedArea.priceSections.length
                      ? "ring-1 ring-[color:var(--accent-11)]"
                      : ""
                  }
                >
                  <motion.button
                    onClick={() => {
                      if (
                        selectedPriceSectionIds &&
                        selectedPriceSectionIds.includes(section.id)
                      ) {
                        setSelectedPriceSectionIds((prev) =>
                          prev && prev.length > 1
                            ? (prev ?? []).filter((id) => id !== section.id)
                            : null
                        );
                      } else {
                        if (
                          selectedPriceSectionIds &&
                          selectedPriceSectionIds.length ===
                            selectedArea.priceSections.length - 1
                        ) {
                          setSelectedPriceSectionIds(null);
                        } else {
                          setSelectedPriceSectionIds((prev) => [
                            ...(prev ?? []),
                            section.id,
                          ]);
                        }
                      }
                    }}
                    whileTap={{
                      scale: 0.95,
                    }}
                  >
                    <Text
                      as="span"
                      weight="medium"
                      shopsiteClasses="font-medium text-black"
                    >
                      {section.name}
                    </Text>
                  </motion.button>
                </Card>
              ))}
            </nav>
          </div>
        )}
      <Heading
        as="h2"
        size="4"
        weight="bold"
        asChild
        sharedClasses="truncate"
        shopsiteClasses="text-lg font-semibold text-black"
      >
        <motion.h2
          ref={targetRef}
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={
            selectedArea !== null && !showAddons
              ? {
                  opacity: 1,
                  height: "auto",
                  marginTop:
                    areas.length > 1 ||
                    showing.area.gaHasExtras ||
                    (selectedArea.type === "allocated" &&
                      selectedArea.priceSections.length > 1)
                      ? 16
                      : 0,
                  marginBottom: 8,
                }
              : { opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }
          }
        >
          {t("areapicker.areatickets")}
        </motion.h2>
      </Heading>
      <AnimatePresence initial={false}>
        {selectedArea && !showAddons && (
          <motion.div
            initial="hide"
            animate="show"
            exit="hide"
            variants={{
              hide: {
                opacity: 0,
                height: 0,
                transition: {
                  height: {
                    delay: 0.1,
                  },
                },
              },
              show: {
                opacity: 1,
                height: "auto",
                transition: {
                  height: { delay: 0.1 },
                  opacity: { delay: 0.3 },
                },
              },
            }}
          >
            {selectedArea.type === "ga" ? (
              <EventDisplayGA
                key={selectedArea.id}
                eventId={eventId}
                eventName={eventName}
                eventVenueId={eventVenueId}
                orgUid={orgUid}
                showingId={showingId}
                selectedConnectedShowings={selectedConnectedShowings}
                gaAreaIds={selectedArea.areaIds}
                isExtraAddOns={showAddons}
                priceSectionIds={selectedArea.priceSections.map(
                  (pricesection) => pricesection.id
                )}
                basketTickets={basketTickets ?? []}
                createBasket={createBasket}
                analyticsConfigs={analyticsConfigs}
                landingPages={landingPages}
                onAddToCart={onAddToCart}
              />
            ) : (
              <AllocatingMap
                eventId={eventId}
                eventName={eventName}
                eventVenueId={eventVenueId}
                orgUid={orgUid}
                areaId={selectedArea.areaIds[0]}
                priceSectionIds={
                  showing.area.groupMode === ShowingGroupMode.GROUPED
                    ? selectedPriceSectionIds
                    : selectedArea.priceSections.map(
                        (pricesection) => pricesection.id
                      )
                }
                showingId={showingId}
                selectedConnectedShowings={selectedConnectedShowings}
                areaName={selectedArea?.name}
                analyticsConfigs={analyticsConfigs}
                currency={currency}
                landingPages={landingPages}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {(selectedArea || showAddons) && showing.area.gaHasExtras ? (
        <>
          <Heading
            as="h2"
            size="4"
            weight="bold"
            sharedClasses="h-11 truncate mt-4"
            shopsiteClasses="text-lg font-semibold text-black"
          >
            {t("areapicker.extraaddonstickets")}
          </Heading>
          <EventDisplayGA
            eventId={eventId}
            eventName={eventName}
            eventVenueId={eventVenueId}
            orgUid={orgUid}
            showingId={showingId}
            selectedConnectedShowings={selectedConnectedShowings}
            gaAreaIds={availableAreas?.ga.map((ga) => ga.gaarea.id) ?? []}
            isExtraAddOns={true}
            priceSectionIds={
              availableAreas?.ga.map((ga) => ga.pricesection.id) ?? []
            }
            basketTickets={basketTickets ?? []}
            createBasket={createBasket}
            analyticsConfigs={analyticsConfigs}
            landingPages={landingPages}
            onAddToCart={onAddToCart}
          />
        </>
      ) : (
        <></>
      )}
    </>
  );
}
